/**
 * Recipe Editor V2 - Main Component
 *
 * Advanced recipe editor with token support and AI suggestions
 *
 * Features:
 * - Token-based editing: quantity and ingredient tokens with visual chips
 * - Autocomplete for ingredients
 * - Draft mode for ingredient entry
 * - AI-powered suggestions with accept/discard UI
 * - Smart copy/paste with token preservation
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

import {EditorState} from "@codemirror/state";
import {EditorView, keymap} from "@codemirror/view";
import {defaultKeymap} from "@codemirror/commands";
import {autocompletion, startCompletion} from "@codemirror/autocomplete";
import {markdown} from "@codemirror/lang-markdown";

// Models and Types
import {Suggestion} from "./models/editor.types";

// State Management
import {tokensField, setTokensEffect, suggestionsField, setSuggestionsEffect} from "./state/editor.state";

// Decorations
import {
  tokenDecorations,
  tokenAtomicDeco,
  tokenAtomicExtension,
  suggestionDecorations
} from "./decorations/editor.decorations";

// Suggestions
import {suggestionHoverTooltip} from "./suggestions/suggestion-menu";

// Serialization
import {parseSerializedRecipe, serializeRecipe} from "./serialization/recipe-serializer";

// Autocomplete
import {ingredientCompletionSource, normalizeIngredientList} from "./autocomplete/ingredient-autocomplete";

// Clipboard
import {createClipboardCopyHandler, createClipboardPasteHandler} from "./clipboard/clipboard-handlers";

// Keymaps and Handlers
import {
  createAcceptCompletionKeymap,
  createCancelDraftKeymap,
  createCommitHandlers,
  createDraftEngine
} from "./keymaps/editor.keymaps";

import {
  createSuggestionClickHandler,
  createBlurHandler,
  createChangeListener,
  createSelectionTokenMenuHandler
} from "./handlers/editor.handlers";

// Utils
import {getDraftNearCursor} from "./utils/draft.utils";

// Extensions
import {tooltipPortal} from "./extensions/tooltip-portal";
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: "app-recipe-editor-v2",
  standalone: true,
  imports: [],
  template: `
    <div class="toolbar">
      @if (debug) {
        <button type="button" (click)="testSuggestions()">🔍 Test Suggestions</button>
      }
      @if (debug) {
        <button type="button" (click)="exportTagged()">Export tagged</button>
      }
      @if (debug) {
        <button type="button" (click)="clearDebug()">Clear debug</button>
      }
    </div>

    <div class="editor" #host></div>

    @if (debug && exported) {
      <pre class="output">{{ exported }}</pre>
    }

    @if (debug) {
      <div class="debug">
        <div class="debug-title">Debug (latest first)</div>
        @for (l of debugLogs; track l) {
          <div class="debug-item">{{ l }}</div>
        }
      </div>
    }
    `,
  styleUrls: ['./recipe-editor-v2.component.scss']
})
export class RecipeEditorV2Component implements AfterViewInit, OnDestroy, OnChanges {
  @Input() serializedText = "";
  @Output() serializedTextChange = new EventEmitter<string>();
  @Input() serializedTextChangeDebounceMs = 200;
  @Input() autocompleteIngredients: string[] = [];
  @Input() emitOnBlur = false;
  @Output() onBlur = new EventEmitter<string>();
  @Input() debug = false;
  @Input() singleLine = true;
  @Input() minHeight: string = "80px";

  @ViewChild("host", {static: true}) host!: ElementRef<HTMLDivElement>;

  private view!: EditorView;
  exported = "";
  debugLogs: string[] = [];
  private normalizedIngredients: string[] = [];
  private isApplyingExternalSerializedText = false;
  private pendingEmitTimer: number | null = null;

  private readonly appLog: AppLogger;

  constructor(
    private appLogger: AppLoggerService,
  ) {
    this.appLog = this.appLogger.createForClass(RecipeEditorV2Component);
  }

  /**
   * Push suggestions to the editor from outside
   */
  setSuggestions(suggestions: Suggestion[]): void {
    if (!this.view) return;
    this.view.dispatch({effects: setSuggestionsEffect.of(suggestions)});
  }

  /**
   * Test suggestions feature
   */
  testSuggestions(): void {
    const text = this.view.state.doc.toString();
    const suggestions: Suggestion[] = [];
    let count = 0;
    const maxSuggestions = 5;

    const words = text.split(/\s+/);
    let pos = 0;

    for (const word of words) {
      if (count >= maxSuggestions) break;

      const from = text.indexOf(word, pos);
      if (from === -1) continue;

      const to = from + word.length;
      pos = to;

      if (word.length >= 3 && /^[a-zäöüß]/i.test(word)) {
        suggestions.push({from, to, suggestedType: "ingredient"});
        count++;
      }
    }

    this.setSuggestions(suggestions);
  }

  private log = (msg: string, data?: any) => {
    if (!this.debug) return;

    const line = data ? `${msg} ${JSON.stringify(data)}` : msg;
    this.appLog.debug('RecipeEditorV2 debug', { line, msg, data });
    this.debugLogs.unshift(line);
    if (this.debugLogs.length > 200) this.debugLogs.pop();
  };

  private scheduleSerializedEmit(view: EditorView): void {
    if (this.isApplyingExternalSerializedText) return;

    const emitNow = () => {
      const next = this.serializeTaggedFromState(view.state);
      this.serializedTextChange.emit(next);
    };

    const ms = Math.max(0, Number(this.serializedTextChangeDebounceMs ?? 0) || 0);

    if (ms === 0) {
      emitNow();
      return;
    }

    if (this.pendingEmitTimer != null) {
      window.clearTimeout(this.pendingEmitTimer);
    }

    this.pendingEmitTimer = window.setTimeout(() => {
      this.pendingEmitTimer = null;
      emitNow();
    }, ms);
  }

  /**
   * Force emitting the current serialized text immediately.
   *
   * Use this from a parent (e.g. before saving) to capture the latest editor state
   * even if the user never blurred the editor.
   */
  flush(options?: { emitBlur?: boolean }): void {
    if (!this.view) return;
    if (this.isApplyingExternalSerializedText) return;

    if (this.pendingEmitTimer != null) {
      window.clearTimeout(this.pendingEmitTimer);
      this.pendingEmitTimer = null;
    }

    const serialized = this.serializeTaggedFromState(this.view.state);
    this.serializedTextChange.emit(serialized);

    if (options?.emitBlur) {
      this.onBlur.emit(serialized);
    }
  }

  private serializeTaggedFromState(state: EditorState): string {
    const text = state.doc.toString();
    const tokens = [...state.field(tokensField)].sort((a, b) => a.from - b.from);
    return serializeRecipe(text, tokens);
  }

  ngAfterViewInit(): void {
    this.host.nativeElement.style.setProperty("--recipe-editor-min-height", this.minHeight);

    // Initialize ingredient list
    this.normalizedIngredients = normalizeIngredientList(this.autocompleteIngredients);

    // Parse initial input
    const initial = this.serializedText?.trim().length > 0
      ? parseSerializedRecipe(this.serializedText)
      : {text: ``, tokens: []};

    // Create editor extensions
    const emitOnChange = createChangeListener(
      this.emitOnBlur,
      (view) => this.scheduleSerializedEmit(view)
    );

    const emitOnBlurHandler = createBlurHandler(
      (serialized) => this.onBlur.emit(serialized),
      (state) => this.serializeTaggedFromState(state),
      this.emitOnBlur,
      (view) => this.scheduleSerializedEmit(view)
    );

    const cancelDraftKeymap = createCancelDraftKeymap(this.log);
    const acceptCompletionKeymap = createAcceptCompletionKeymap(this.singleLine, this.log);
    const commitHandlers = createCommitHandlers(this.log);
    const draftEngine = createDraftEngine(this.log);
    const suggestionClickHandler = createSuggestionClickHandler();
    const selectionTokenMenuHandler = createSelectionTokenMenuHandler();
    const clipboardCopyHandlers = createClipboardCopyHandler();
    const clipboardPasteHandlers = createClipboardPasteHandler();

    // Create editor state
    const state = EditorState.create({
      doc: initial.text,
      extensions: [
        markdown(),
        EditorView.editorAttributes.of({tabindex: "0"}),
        EditorView.contentAttributes.of({spellcheck: "false"}),
        EditorView.lineWrapping,

        // Keymaps
        cancelDraftKeymap,
        acceptCompletionKeymap,
        commitHandlers,
        keymap.of(defaultKeymap),

        // State and Decorations
        tokensField,
        tokenDecorations,
        tokenAtomicDeco,
        tokenAtomicExtension,
        suggestionsField,
        suggestionDecorations,
        suggestionHoverTooltip,
        suggestionClickHandler,
        selectionTokenMenuHandler,

        // Clipboard
        clipboardCopyHandlers,
        clipboardPasteHandlers,

        // Lifecycle handlers
        emitOnBlurHandler,

        // Tooltip portal (renders autocomplete at body level)
        tooltipPortal,

        // Autocomplete
        autocompletion({
          override: [(ctx) => ingredientCompletionSource(this.normalizedIngredients, this.log)(ctx)],
        }),

        // Draft engine
        draftEngine,
        emitOnChange,
      ],
    });

    this.view = new EditorView({state, parent: this.host.nativeElement});

    // Apply initial tokens
    if (initial.tokens.length > 0) {
      this.view.dispatch({effects: setTokensEffect.of(initial.tokens)});
      this.log("🧩 init from serializedText", {tokenCount: initial.tokens.length});
    }

    this.scheduleSerializedEmit(this.view);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["minHeight"] && this.host) {
      this.host.nativeElement.style.setProperty("--recipe-editor-min-height", this.minHeight);
    }

    if (changes["autocompleteIngredients"]) {
      this.normalizedIngredients = normalizeIngredientList(this.autocompleteIngredients);

      if (this.view) {
        const tokens = this.view.state.field(tokensField);
        const pos = this.view.state.selection.main.head;
        const draft = getDraftNearCursor(tokens, pos);

        if (draft) {
          startCompletion(this.view);
        }
      }
    }

    if (!this.view) return;
    if (!changes["serializedText"]) return;

    const raw = (this.serializedText ?? "").toString();
    const parsed = parseSerializedRecipe(raw);

    const curLen = this.view.state.doc.length;

    this.log("🔁 input change -> rehydrate", {
      newDocLen: parsed.text.length,
      tokenCount: parsed.tokens.length
    });

    this.isApplyingExternalSerializedText = true;
    try {
      this.view.dispatch({
        changes: {from: 0, to: curLen, insert: parsed.text},
        effects: setTokensEffect.of(parsed.tokens),
        selection: {anchor: 0, head: 0},
        scrollIntoView: true,
      });
    } finally {
      this.isApplyingExternalSerializedText = false;
    }

    this.scheduleSerializedEmit(this.view);
  }

  ngOnDestroy(): void {
    if (this.pendingEmitTimer != null) {
      window.clearTimeout(this.pendingEmitTimer);
    }
    this.view?.destroy();
  }

  exportTagged(): void {
    this.exported = this.serializeTaggedFromState(this.view.state);
    this.log("📤 export", {tokens: [...this.view.state.field(tokensField)]});
  }

  clearDebug(): void {
    if (!this.debug) return;
    this.debugLogs = [];
  }
}
